import MapIcon from "@mui/icons-material/Map";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SourceIcon from "@mui/icons-material/Source";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useMemo } from "react";

import BarChartCustom from "../../../../components/shared/chart/BarChartCustom";
import PieChartCustom from "../../../../components/shared/chart/PieChartCustom";
import { formatNumber } from "../../../../../infrastructure/utils/formatters";
import { buildDrillDownHref, pick } from "./managementDashboardUtils";

const CARD_SX = { borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" };

const normalizeBreakdownItems = (items) =>
  (items || []).map((item) => ({
    code: pick(item, "code", "Code") || "",
    label: pick(item, "label", "Label") || "—",
    count: Number(pick(item, "count", "Count") ?? 0),
    drillDown: pick(item, "drillDown", "DrillDown"),
  }));

const SectionShell = ({ title, icon: Icon, loading, error, onRetry, children }) => (
  <Card sx={{ ...CARD_SX, height: "100%" }}>
    <CardHeader
      avatar={Icon ? <Icon color="primary" /> : null}
      title={title}
      titleTypographyProps={{ fontSize: 15, fontWeight: 700 }}
      sx={{ pb: 0, pt: 1.5, px: 2 }}
      action={
        error ? (
          <Button size="small" onClick={onRetry}>
            تلاش مجدد
          </Button>
        ) : null
      }
    />
    <CardContent sx={{ py: 1, px: 2 }}>
      {loading ? (
        <Skeleton variant="rounded" height={280} />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        children
      )}
    </CardContent>
  </Card>
);

const AcquisitionBreakdownSection = ({
  loading,
  error,
  data,
  onRetry,
}) => {
  const totalCount = Number(pick(data, "totalRequestCount", "TotalRequestCount") ?? 0);

  const byProvince = useMemo(
    () => normalizeBreakdownItems(pick(data, "byProvince", "ByProvince")),
    [data]
  );

  const byRegistrationSource = useMemo(
    () => normalizeBreakdownItems(pick(data, "byRegistrationSource", "ByRegistrationSource")),
    [data]
  );

  const byRepresentative = useMemo(
    () => normalizeBreakdownItems(pick(data, "byRepresentative", "ByRepresentative")),
    [data]
  );

  const provinceChartData = byProvince.map((item) => ({
    x: item.label,
    تعداد: item.count,
  }));

  const representativeChartData = byRepresentative.map((item) => ({
    x: item.label.length > 24 ? `${item.label.slice(0, 24)}…` : item.label,
    تعداد: item.count,
    fullLabel: item.label,
  }));

  const sourcePieData = byRegistrationSource.map((item) => ({
    name: item.label,
    value: item.count,
  }));

  const pct = (count) =>
    totalCount > 0 ? `${formatNumber((count / totalCount) * 100, 1)}٪` : "—";

  return (
    <Box sx={{ mt: 2.5 }}>
      <Typography fontWeight={700} fontSize={16} sx={{ mb: 1.5 }}>
        تفکیک جذب و ثبت‌نام متقاضی
      </Typography>

      <Grid container spacing={1.5}>
        <Grid item xs={12} lg={7}>
          <SectionShell
            title="تفکیک استان"
            icon={MapIcon}
            loading={loading}
            error={error}
            onRetry={onRetry}
          >
            <Box sx={{ height: 320 }}>
              <BarChartCustom
                title={`${formatNumber(totalCount)} درخواست در بازه انتخابی`}
                xAxis="x"
                xAxisTitle="استان"
                yAxisTitle="تعداد درخواست"
                data={provinceChartData}
                series={["تعداد"]}
                colors={["#1976d2"]}
              />
            </Box>
            {byProvince.length > 0 ? (
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                {byProvince.slice(0, 6).map((item) => {
                  const href = buildDrillDownHref(item.drillDown);
                  return href ? (
                    <Button
                      key={item.code}
                      size="small"
                      variant="outlined"
                      component={RouterLink}
                      to={href}
                    >
                      {item.label}: {formatNumber(item.count)}
                    </Button>
                  ) : null;
                })}
              </Stack>
            ) : null}
          </SectionShell>
        </Grid>

        <Grid item xs={12} lg={5}>
          <SectionShell
            title="منبع ثبت‌نام متقاضی"
            icon={SourceIcon}
            loading={loading}
            error={error}
            onRetry={onRetry}
          >
            <Grid container spacing={1}>
              <Grid item xs={12} md={6}>
                <Box sx={{ height: 240 }}>
                  <PieChartCustom
                    title="سهم هر منبع"
                    data={sourcePieData}
                    colors={["#ed6c02", "#9c27b0", "#2e7d32"]}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>منبع</TableCell>
                      <TableCell align="right">تعداد</TableCell>
                      <TableCell align="right">سهم</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byRegistrationSource.map((item) => {
                      const href = buildDrillDownHref(item.drillDown);
                      return (
                        <TableRow key={item.code} hover={Boolean(href)}>
                          <TableCell>
                            {href ? (
                              <Typography
                                component={RouterLink}
                                to={href}
                                sx={{ color: "primary.main", textDecoration: "none" }}
                              >
                                {item.label}
                              </Typography>
                            ) : (
                              item.label
                            )}
                          </TableCell>
                          <TableCell align="right">{formatNumber(item.count)}</TableCell>
                          <TableCell align="right">{pct(item.count)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              «لینک پیمانکار» شامل ثبت با لینک معرفی یا ثبت همراه پیمانکار است. «بازاریاب / نماینده»
              شامل لینک نماینده یا ثبت همراه بازاریاب است. «ثبت عادی» سایر موارد است.
            </Typography>
          </SectionShell>
        </Grid>

        <Grid item xs={12}>
          <SectionShell
            title="مقایسه نمایندگان (تعداد متقاضی ثبت‌شده)"
            icon={PeopleAltIcon}
            loading={loading}
            error={error}
            onRetry={onRetry}
          >
            {byRepresentative.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                در بازه انتخابی، درخواستی با انتساب به نماینده/بازاریاب ثبت نشده است.
              </Typography>
            ) : (
              <>
                <Box sx={{ height: Math.max(280, byRepresentative.length * 36) }}>
                  <BarChartCustom
                    title="۱۵ نماینده برتر بر اساس تعداد درخواست"
                    xAxis="x"
                    xAxisTitle="نماینده"
                    yAxisTitle="تعداد درخواست"
                    data={representativeChartData}
                    series={["تعداد"]}
                    colors={["#6a1b9a"]}
                    customTooltips={representativeChartData.map((item) => item.fullLabel)}
                  />
                </Box>
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell width={56}>#</TableCell>
                      <TableCell>نماینده</TableCell>
                      <TableCell align="right">تعداد متقاضی</TableCell>
                      <TableCell align="right">سهم از کل</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byRepresentative.map((item, index) => {
                      const href = buildDrillDownHref(item.drillDown);
                      return (
                        <TableRow key={item.code} hover={Boolean(href)}>
                          <TableCell>{formatNumber(index + 1)}</TableCell>
                          <TableCell>
                            {href ? (
                              <Typography
                                component={RouterLink}
                                to={href}
                                sx={{ color: "primary.main", textDecoration: "none" }}
                              >
                                {item.label}
                              </Typography>
                            ) : (
                              item.label
                            )}
                          </TableCell>
                          <TableCell align="right">{formatNumber(item.count)}</TableCell>
                          <TableCell align="right">{pct(item.count)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </>
            )}
          </SectionShell>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AcquisitionBreakdownSection;
